import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import type { GenerateStickerOptions } from '../../models/generate-sticker-options.model';

import {
  activityInstancesGenerateCoverRequest,
  activityInstancesGenerateStickerRequest,
} from '@api/@angular/common.gen';
import {
  activityInstancesCopy,
  activityInstancesCreateEmpty,
  activityInstancesDelete,
  activityInstancesDetail,
  activityInstancesLinkContextDocuments,
  activityInstancesUnlinkContextDocument,
  activityInstancesUpdateDescription,
  activityInstancesUpdateDuration,
  activityInstancesUpdateFieldLayout,
  activityInstancesUpdateInstructions,
  activityInstancesUpdateName,
  activityInstancesUpdateSections,
  activityInstancesUpdateSubTopic,
  activityInstancesUpdateTags,
  activityInstancesCreateActivityPedagogicalCustomField,
  activityInstancesDeleteActivityPedagogicalCustomField,
  activityInstancesGetActivityPedagogicalCustomFields,
  activityInstancesGetActivityPedagogicalFields,
  activityInstancesReorderActivityPedagogicalCustomFields,
  activityInstancesReorderActivityPedagogicalFields,
  activityInstancesUpdateActivityPedagogicalCustomField,
  activityInstancesUpdateActivityPedagogicalCustomFieldValue,
  activityInstancesUpdateActivityPedagogicalCustomFieldVisibility,
  activityInstancesUpdateActivityPedagogicalFieldValue,
  activityInstancesUpdateActivityPedagogicalFieldVisibility,
  activityInstancesUpdateCoverFraming,
  activityInstancesUpdateVisibility,
} from '@api/sdk.gen';
import type {
  ActivityContentSection,
  ActivityCover,
  ActivityCoverGenerationResult,
  ActivityFieldLayoutItem,
  ActivityInstanceDetail,
  ActivityPedagogicalCustomFieldList,
  ActivityPedagogicalCustomFieldResponse,
  ActivityPedagogicalFieldList,
  ActivityPedagogicalFieldResponse,
  CreateActivityPedagogicalCustomFieldRequest,
  ActivityStickerGeneration,
  ActivityVisibility,
  CopyActivityResult,
  CreateEmptyActivityResult,
  GenerateActivityStickerRequest,
  InstructionStep,
  PedagogicalFieldName,
  UpdateActivityCoverFramingRequest,
  UpdateActivityInstanceDurationRequest,
  UpdateActivityPedagogicalCustomFieldRequest,
} from '@api';

import { sendCancellable } from './send-cancellable';

@Injectable({ providedIn: 'root' })
export class ActivityInstancesApiService {
  readonly #httpClient = inject(HttpClient);

  getById(id: string): Promise<ActivityInstanceDetail> {
    return activityInstancesDetail({
      path: { id },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  delete(id: string): Promise<void> {
    return activityInstancesDelete({
      path: { id },
      throwOnError: true,
      responseStyle: 'data' as const,
    }).then(() => undefined);
  }

  copy(id: string): Promise<string> {
    return (
      activityInstancesCopy({
        path: { id },
        throwOnError: true,
        responseStyle: 'data' as const,
      }) as unknown as Promise<CopyActivityResult>
    ).then((result) => result.id);
  }

  createEmpty(): Promise<string> {
    return (
      activityInstancesCreateEmpty({
        throwOnError: true,
        responseStyle: 'data' as const,
      }) as unknown as Promise<CreateEmptyActivityResult>
    ).then((result) => result.id);
  }

  updateName(id: string, value: string): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateName({
      path: { id },
      body: { value },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateVisibility(id: string, visibility: ActivityVisibility): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateVisibility({
      path: { id },
      body: { visibility },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateDescription(id: string, value: string): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateDescription({
      path: { id },
      body: { value },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateInstructions(id: string, value: InstructionStep[]): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateInstructions({
      path: { id },
      body: { value },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateFieldLayout(id: string, value: ActivityFieldLayoutItem[]): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateFieldLayout({
      path: { id },
      body: { value },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateSections(id: string, value: ActivityContentSection[]): Promise<ActivityInstanceDetail> {
    // The write payload carries only `{ title, contentHtml }`; the server-generated `id` is
    // dropped here (it is assigned on the server and reconciled back from the response).
    const writeItems = value.map((section) => ({ title: section.title, contentHtml: section.contentHtml }));

    return activityInstancesUpdateSections({
      path: { id },
      body: { value: writeItems },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateDuration(
    id: string,
    duration: UpdateActivityInstanceDurationRequest,
  ): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateDuration({
      path: { id },
      body: duration,
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateTags(id: string, tagIds: string[]): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateTags({
      path: { id },
      body: { value: tagIds },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  updateSubTopic(id: string, subTopicId: string): Promise<ActivityInstanceDetail> {
    return activityInstancesUpdateSubTopic({
      path: { id },
      body: { value: subTopicId },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  /**
   * Links a batch of Kontextus documents to an owned activity instance (KT-200.4, contract C10).
   * POSTs the whole `documentIds` list in a single call through the generated SDK link builder —
   * an immediate mutation, NOT the debounced `commitField` path — and returns the refreshed detail.
   */
  linkContextDocuments(id: string, documentIds: string[]): Promise<ActivityInstanceDetail> {
    return activityInstancesLinkContextDocuments({
      path: { id },
      body: { documentIds },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  /**
   * Unlinks a single Kontextus document from an owned activity instance (KT-200.4, contract C10).
   * DELETEs the join through the generated SDK builder and returns the refreshed detail. This only
   * breaks the activity↔document link; the file itself stays in the Dokumentumtár.
   */
  unlinkContextDocument(id: string, documentId: string): Promise<ActivityInstanceDetail> {
    return activityInstancesUnlinkContextDocument({
      path: { id, documentId },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityInstanceDetail>;
  }

  /**
   * Goes through the generated request builder and `HttpClient` instead of the SDK promise
   * function: only an `HttpClient` subscription can be unsubscribed, which is what makes an abort
   * really cancel the HTTP request. There is deliberately no client timeout.
   *
   * @returns The created generation. Rejects with the `HttpErrorResponse` (read it with
   *   `httpErrorStatus` / `httpErrorTitle`), or with the signal's reason when aborted.
   */
  generateSticker(
    id: string,
    options: GenerateStickerOptions = {},
  ): Promise<ActivityStickerGeneration> {
    const { teacherRequest, signal } = options;

    // The route requires a body, so "no request" is sent as `{}`.
    const body: GenerateActivityStickerRequest = teacherRequest?.trim() ? { teacherRequest } : {};
    const request = activityInstancesGenerateStickerRequest({
      path: { id },
      body,
      // The request builder sends the client's default JSON Content-Type on every request; named here so
      // this JSON route does not depend on that default.
      headers: { 'Content-Type': 'application/json' },
      httpClient: this.#httpClient,
    });

    return sendCancellable<ActivityStickerGeneration>(this.#httpClient, request, signal);
  }

  /**
   * Same cancellation mechanics as {@link generateSticker}. The route takes no body, so the request
   * carries neither a body nor a `Content-Type`.
   *
   * @returns The new cover and its generation record. Rejects with the `HttpErrorResponse`, or with
   *   the signal's reason when aborted.
   */
  generateCover(id: string, options: { signal?: AbortSignal } = {}): Promise<ActivityCoverGenerationResult> {
    const request = activityInstancesGenerateCoverRequest({
      path: { id },
      // The request builder sends the client's default JSON Content-Type on every request, and its
      // body-less clean-up discards the result of an immutable `HttpHeaders.delete`, so the default
      // survives unless it is nulled here.
      headers: { 'Content-Type': null },
      httpClient: this.#httpClient,
    });

    return sendCancellable<ActivityCoverGenerationResult>(this.#httpClient, request, options.signal);
  }

  /**
   * Saves how the owner framed the shown cover. Never cancelled: a save the teacher confirmed must reach the server.
   *
   * @returns The saved cover with the stored values. Rejects with the ProblemDetails body, not an `HttpErrorResponse`
   *   (read it with `httpErrorStatus` / `httpErrorTitle`); a 409 with the code `ActivityCover.CoverChanged` means the
   *   cover was replaced since it was loaded.
   */
  updateCoverFraming(id: string, framing: UpdateActivityCoverFramingRequest): Promise<ActivityCover> {
    return activityInstancesUpdateCoverFraming({
      path: { id },
      body: framing,
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityCover>;
  }

  getPedagogicalFields(id: string): Promise<ActivityPedagogicalFieldList> {
    return activityInstancesGetActivityPedagogicalFields({
      path: { id },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalFieldList>;
  }

  updatePedagogicalFieldValue(
    id: string,
    fieldName: PedagogicalFieldName,
    value: string | null,
  ): Promise<ActivityPedagogicalFieldResponse> {
    return activityInstancesUpdateActivityPedagogicalFieldValue({
      path: { id, fieldName },
      body: { value },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalFieldResponse>;
  }

  updatePedagogicalFieldVisibility(
    id: string,
    fieldName: PedagogicalFieldName,
    visible: boolean,
  ): Promise<ActivityPedagogicalFieldList> {
    return activityInstancesUpdateActivityPedagogicalFieldVisibility({
      path: { id },
      body: { fieldName, visible },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalFieldList>;
  }

  reorderPedagogicalField(
    id: string,
    fieldName: PedagogicalFieldName,
    position: number,
  ): Promise<ActivityPedagogicalFieldList> {
    return activityInstancesReorderActivityPedagogicalFields({
      path: { id },
      body: { fieldName, position },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalFieldList>;
  }

  getPedagogicalCustomFields(id: string): Promise<ActivityPedagogicalCustomFieldList> {
    return activityInstancesGetActivityPedagogicalCustomFields({
      path: { id },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalCustomFieldList>;
  }

  createPedagogicalCustomField(
    id: string,
    body: CreateActivityPedagogicalCustomFieldRequest,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return activityInstancesCreateActivityPedagogicalCustomField({
      path: { id },
      body,
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalCustomFieldList>;
  }

  updatePedagogicalCustomFieldVisibility(
    id: string,
    fieldId: string,
    visible: boolean,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return activityInstancesUpdateActivityPedagogicalCustomFieldVisibility({
      path: { id, fieldId },
      body: { visible },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalCustomFieldList>;
  }

  updatePedagogicalCustomField(
    id: string,
    fieldId: string,
    body: UpdateActivityPedagogicalCustomFieldRequest,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return activityInstancesUpdateActivityPedagogicalCustomField({
      path: { id, fieldId },
      body,
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalCustomFieldList>;
  }

  reorderPedagogicalCustomField(
    id: string,
    fieldId: string,
    position: number,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return activityInstancesReorderActivityPedagogicalCustomFields({
      path: { id },
      body: { id: fieldId, position },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalCustomFieldList>;
  }

  updatePedagogicalCustomFieldValue(
    id: string,
    fieldId: string,
    value: string | null,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return activityInstancesUpdateActivityPedagogicalCustomFieldValue({
      path: { id, fieldId },
      body: { value },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalCustomFieldList>;
  }

  deletePedagogicalCustomField(id: string, fieldId: string): Promise<ActivityPedagogicalCustomFieldList> {
    return activityInstancesDeleteActivityPedagogicalCustomField({
      path: { id, fieldId },
      throwOnError: true,
      responseStyle: 'data' as const,
    }) as unknown as Promise<ActivityPedagogicalCustomFieldList>;
  }
}
